/**
 * ZFreezePanel — the Z-freeze close (blind count → frozen Z) and the audited reopen.
 *
 * The close persists + FREEZES the Z (theoretical/real/ecart). It is born-immutable: a later edit to
 * a payment or movement can never change it. A reopen (within the config window) is fully audited and
 * retains the prior frozen Z. Both actions are gated by their server capability via useCan.
 */
import { useCallback, useState, type FormEvent } from "react";
import { Badge, Button, Card, FormField, Modal, Textarea, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { token } from "@/design/theme";
import { FINANCE_CAPS, useCloseZ, useReopen } from "../api";
import type { CloseZResult, PriorZ } from "../types";
import { MoneyInput } from "./MoneyInput";
import { Money } from "./Money";

export interface ZFreezePanelProps {
  labId: string | null;
  sessionId: string;
  /** Refetch the X-report / session after a close or reopen. */
  onChanged: () => void;
}

export function ZFreezePanel({ labId, sessionId, onChanged }: ZFreezePanelProps) {
  const { t } = useT();
  const toast = useToast();
  const canClose = useCan(FINANCE_CAPS.caisseClose);
  const canReopen = useCan(FINANCE_CAPS.caisseReopen);

  const closeMut = useCloseZ(labId);
  const reopenMut = useReopen(labId);

  const [comptees, setComptees] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);
  const [frozen, setFrozen] = useState<CloseZResult | null>(null);

  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenMotif, setReopenMotif] = useState("");
  const [reopenTouched, setReopenTouched] = useState(false);
  const [priorZ, setPriorZ] = useState<PriorZ | null>(null);

  const comptesError =
    touched && (comptees == null || comptees < 0) ? t("finance.field.amountInvalid") : null;
  const valid = comptees != null && comptees >= 0;

  const submitClose = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canClose) return;
    closeMut.mutate(
      { sessionId, especes_comptees: comptees ?? 0 },
      {
        onSuccess: (data) => {
          toast.success(t("finance.z.success"));
          setFrozen(data);
          setComptees(null);
          setTouched(false);
          onChanged();
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.z.title"));
        },
      },
    );
  };

  const reopenMotifError =
    reopenTouched && reopenMotif.trim().length < 3 ? t("finance.field.motifShort") : null;

  const submitReopen = (e: FormEvent) => {
    e.preventDefault();
    setReopenTouched(true);
    if (reopenMotif.trim().length < 3 || !canReopen) return;
    reopenMut.mutate(
      { sessionId, motif: reopenMotif.trim() },
      {
        onSuccess: (data) => {
          toast.success(t("finance.reopen.success"));
          setPriorZ(data.prior_z);
          setReopenOpen(false);
          setReopenMotif("");
          setReopenTouched(false);
          setFrozen(null);
          onChanged();
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.reopen.title"));
        },
      },
    );
  };

  // Stable close handler so the Modal focus-trap effect doesn't steal focus from the motif field
  // mid-typing (the effect re-runs on a new onClose identity).
  const closeReopen = useCallback(() => {
    setReopenOpen(false);
    setReopenMotif("");
    setReopenTouched(false);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card title={t("finance.z.title")}>
        <form onSubmit={submitClose} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)", maxWidth: 480 }}>
            <p
              role="note"
              style={{
                margin: 0,
                padding: "var(--nv-s-3)",
                borderRadius: "var(--nv-r-md)",
                background: token.tintHigh,
                color: token.warn,
                fontSize: "var(--nv-fs-label)",
              }}
            >
              ▲ {t("finance.z.warn")}
            </p>

            <FormField
              label={t("finance.z.comptees")}
              hint={t("finance.z.comptees.hint")}
              required
              {...(comptesError ? { error: comptesError } : {})}
            >
              {(p) => (
                <MoneyInput
                  {...p}
                  aria-label={t("finance.amount.aria")}
                  valueCentimes={comptees}
                  onChangeCentimes={setComptees}
                  invalid={!!comptesError}
                />
              )}
            </FormField>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="submit" variant="danger" loading={closeMut.isPending} disabled={!canClose}>
                {t("finance.z.submit")}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {frozen && (
        <Card
          title={t("finance.z.result.title")}
          actions={
            <Badge tone={frozen.flagged ? "danger" : "success"}>
              {frozen.flagged ? t("finance.z.flagged") : t("finance.z.notFlagged")}
            </Badge>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--nv-s-2)", margin: 0 }}>
              <dt style={{ color: token.slate }}>{t("finance.z.theorique")}</dt>
              <dd style={{ margin: 0, textAlign: "end" }}>
                <Money centimes={frozen.total_theorique} />
              </dd>
              <dt style={{ color: token.slate }}>{t("finance.z.reel")}</dt>
              <dd style={{ margin: 0, textAlign: "end" }}>
                <Money centimes={frozen.total_reel} />
              </dd>
              <dt style={{ color: token.slate, paddingTop: "var(--nv-s-2)" }}>{t("finance.z.ecart")}</dt>
              <dd style={{ margin: 0, textAlign: "end", paddingTop: "var(--nv-s-2)" }}>
                <EcartBadge ecart={frozen.ecart} />
              </dd>
            </dl>

            <div style={{ display: "flex", gap: "var(--nv-s-2)" }}>
              <Button
                variant="secondary"
                onClick={() => setReopenOpen(true)}
                disabled={!canReopen}
              >
                {t("finance.reopen.cta")}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {priorZ && (
        <Card title={t("finance.reopen.priorZ")}>
          <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--nv-s-2)", margin: 0 }}>
            <dt style={{ color: token.slate }}>{t("finance.z.theorique")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              <Money centimes={priorZ.total_theorique} />
            </dd>
            <dt style={{ color: token.slate }}>{t("finance.z.reel")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              <Money centimes={priorZ.total_reel} />
            </dd>
            <dt style={{ color: token.slate }}>{t("finance.z.ecart")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              {priorZ.ecart == null ? <Money centimes={null} /> : <EcartBadge ecart={priorZ.ecart} />}
            </dd>
          </dl>
        </Card>
      )}

      <Modal
        open={reopenOpen}
        onClose={closeReopen}
        title={t("finance.reopen.title")}
        footer={
          <>
            <Button variant="ghost" onClick={closeReopen} disabled={reopenMut.isPending}>
              {t("action.close")}
            </Button>
            <Button
              type="submit"
              form="finance-reopen"
              variant="danger"
              loading={reopenMut.isPending}
              disabled={!canReopen}
            >
              {t("finance.reopen.submit")}
            </Button>
          </>
        }
      >
        <form id="finance-reopen" onSubmit={submitReopen} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <p
              role="note"
              style={{
                margin: 0,
                padding: "var(--nv-s-3)",
                borderRadius: "var(--nv-r-md)",
                background: token.tintHigh,
                color: token.warn,
                fontSize: "var(--nv-fs-label)",
              }}
            >
              ▲ {t("finance.reopen.warn")}
            </p>
            <FormField
              label={t("finance.reopen.motif")}
              hint={t("finance.reopen.motif.hint")}
              required
              {...(reopenMotifError ? { error: reopenMotifError } : {})}
            >
              {(p) => (
                <Textarea
                  {...p}
                  autoFocus
                  rows={2}
                  value={reopenMotif}
                  invalid={!!reopenMotifError}
                  onChange={(e) => setReopenMotif(e.target.value)}
                />
              )}
            </FormField>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function EcartBadge({ ecart }: { ecart: number }) {
  const { t } = useT();
  if (ecart === 0) return <Badge tone="success">{t("finance.caisse.z.ecart.none")}</Badge>;
  const tone = ecart > 0 ? "info" : "danger";
  const label = ecart > 0 ? t("finance.caisse.z.ecart.surplus") : t("finance.caisse.z.ecart.short");
  return (
    <Badge tone={tone}>
      {label} <Money centimes={ecart} signed />
    </Badge>
  );
}
